import React from 'react';
import Head from '@components/atoms/head/head';
import { defaultMetas } from '@constants/metas/default';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { GetServerSideProps, NextPageWithLayout } from 'next';
import extractFromContext from '@helpers/utils/extract-from-context';
import { usePreferences } from '@context/preferences.context';
import { isRequestError } from '@helpers/errors';
import SimilarProductsList from '@components/similar-products/similar-products-list/similar-products-list';
import { UpdateDataLayerComponent } from '@context/analytics/analytics.context';
import DefaultLayout from '@layouts/default-layout';
import logger from '@helpers/utils/logger/client';
import ProductFeedService from '@services/product-feed-service';
import mapCatalogHitToProduct from '@helpers/utils/catalog/map-catalog-hit-to-product';
import { CatalogProduct } from '@interfaces/models/catalogProduct';
import { useTranslation } from 'next-i18next';
import { getAppInitialProps } from '@helpers/initial-props';
import { getSeasUserHeaders } from '@helpers/utils/catalog/catalog-tracking-utils';
import { getOrCreateLDCookiesSsr } from '@components/bucketTest/bucket.utils';

type SimilarProductsPageProps = {
  similarProducts: CatalogProduct[];
  pageUrl: string;
  title: string;
  feedType: string;
};

const SimilarProductsPage: NextPageWithLayout<SimilarProductsPageProps> = (props) => {
  const { pageUrl, similarProducts } = props;
  const queryParams = new URLSearchParams(pageUrl);
  const isFromFavTile = queryParams.get('isFromFavTile');
  const { t } = useTranslation();
  const title = isFromFavTile === 'true' ? t('PRODUCT.SIMILAR_ITEMS.TITLE') : props.title;
  const subtitle = isFromFavTile === 'true' ? t('FAVORITE_PAGE.ITEMS_TEXT') : '';
  const feedType = props.feedType;

  const { language } = usePreferences();

  const pageMetas = defaultMetas[language];

  return (
    <>
      <Head metadata={pageMetas} />
      <UpdateDataLayerComponent
        data={{
          screen_name: isFromFavTile === 'true' ? '/similar-products-my-favourites' : pageUrl,
          screen_category: 'product_feed_landing_page',
          screen_subcategory: feedType,
          env_template: 'catalog',
        }}
      />
      <SimilarProductsList
        feedType={feedType}
        similarProducts={similarProducts}
        isFromFavTile={isFromFavTile === 'true'}
        title={title}
        subtitle={subtitle}
      />
    </>
  );
};

SimilarProductsPage.Layout = [DefaultLayout];
SimilarProductsPage.PageType = 'SimilarProductsPage';

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { locale, serverAxiosRequestData, preferences, userId } = extractFromContext(context);
  const initialProps = await getAppInitialProps(context);
  const translations = await serverSideTranslations(locale, ['default-layout', 'vc-authentication', 'a11y', 'common']);
  const { serverAnonymousId } = getOrCreateLDCookiesSsr(context);

  const productId = context.query?.productId as string;
  const feedType = (context.query?.feedType as string) ?? 'similar_products';

  if (!productId) {
    return { redirect: { destination: '/', permanent: false } };
  }

  try {
    const similarProductsFeed = await ProductFeedService.getSingleProductFeed(
      {
        productID: parseInt(productId),
        locale: {
          country: preferences?.country,
          language: preferences?.language,
          currency: preferences?.currency,
        },
        pagination: {
          limit: 60,
          offset: 0,
        },
      },
      getSeasUserHeaders(userId, serverAnonymousId),
      feedType,
      serverAxiosRequestData,
    );
    const similarProducts =
      similarProductsFeed?.items?.map((item) =>
        mapCatalogHitToProduct(item, preferences?.country, preferences?.currency, preferences?.language, []),
      ) ?? [];
    return {
      props: {
        ...initialProps,
        ...translations,
        similarProducts: JSON.parse(JSON.stringify(similarProducts)) as CatalogProduct[],
        title: similarProductsFeed?.title ?? '',
        feedType,
        pageUrl: context.req.url,
      },
    };
  } catch (e) {
    // Axios 404 errors should get redirected straight to error page
    if (isRequestError(e)) {
      logger.error(
        e,
        'There was an api request error on the Similar Products Page - getServerSideProps - index.page.tsx',
      );
    } else {
      // Other errors will get logged
      logger.error(e, 'The server failed to render the Similar Products Page - getServerSideProps - index.page.tsx');
    }

    return {
      notFound: true,
    };
  }
};

export default SimilarProductsPage;
